import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import {
  CalendarDays,
  Filter,
  MapPin,
  Navigation,
  Search,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePublicEvents } from "@/lib/queries";
import type { PublicEventRow } from "@/lib/types";

// Cross-org event discovery. No auth. Search + format filter + distance
// filter when the user grants geolocation. JSON-LD + og meta come from the
// per-event page; this list-page also emits an ItemList block for SEO.
export default function PublicEvents() {
  const [q, setQ] = useState("");
  const [format, setFormat] = useState<"" | "singles" | "doubles" | "mixed">("");
  const [near, setNear] = useState<{ lat: number; lng: number } | null>(null);
  const [radius, setRadius] = useState(50);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const { data: events = [], isLoading } = usePublicEvents({
    q: q.trim() || undefined,
    format: format || undefined,
    near: near ? { lat: near.lat, lng: near.lng, radius } : undefined,
  });

  function locate() {
    if (!navigator.geolocation) {
      setGeoError("Browser doesn't support geolocation.");
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNear({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setGeoError(err.message ?? "Location denied.");
        setLocating(false);
      },
      { timeout: 10000 },
    );
  }

  useEffect(() => {
    document.title = "Cornhole events near you — Cornholio";
  }, []);

  return (
    <div className="min-h-dvh bg-background">
      <SeoBlock events={events} />

      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 space-y-2">
          <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
            Cornholio
          </p>
          <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tight">
            Cornhole events
          </h1>
          <p className="text-muted-foreground text-sm">
            Open tournaments, leagues, and drop-ins around you. Org admins
            flag their events public to show up here.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search events, locations, descriptions…"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                <Filter size={11} /> Format
              </span>
              {(["", "doubles", "singles", "mixed"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`px-2.5 py-1 rounded-full border text-xs font-display uppercase tracking-widest ${
                    format === f
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f || "any"}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant={near ? "outline" : "default"}
                onClick={near ? () => setNear(null) : locate}
                disabled={locating}
              >
                <Navigation size={14} />
                {locating
                  ? "Locating…"
                  : near
                    ? `Within ${radius} mi · Clear`
                    : "Near me"}
              </Button>
              {near && (
                <input
                  type="range"
                  min={5}
                  max={250}
                  step={5}
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="flex-1 max-w-sm"
                  aria-label="Distance radius"
                />
              )}
              {geoError && (
                <p className="text-xs text-destructive">{geoError}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading events…</p>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-2">
              <CalendarDays
                className="mx-auto text-muted-foreground"
                size={32}
              />
              <p className="text-sm text-muted-foreground">
                {near
                  ? `No public events within ${radius} miles. Try widening the radius or clearing it.`
                  : "No public events match. Try a broader search."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <PublicEventCard key={e.id} event={e} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function PublicEventCard({ event }: { event: PublicEventRow }) {
  return (
    <li>
      <Link
        href={`/events/${event.orgSlug ?? ""}/${event.slug}`}
        className="block rounded-lg border border-border bg-card hover:bg-muted/40 p-4 space-y-1.5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{event.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {event.orgName ?? "—"} ·{" "}
              <span className="font-display uppercase tracking-widest">
                {event.format}
              </span>
            </div>
          </div>
          <DateBadge date={event.startDate} />
        </div>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
          {event.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={11} /> {event.location}
            </span>
          )}
          {event.distanceMiles != null && (
            <span className="inline-flex items-center gap-1">
              <Navigation size={11} />
              {event.distanceMiles < 1
                ? "< 1 mi"
                : `${event.distanceMiles.toFixed(1)} mi`}
            </span>
          )}
          {event.format === "doubles" && (
            <span className="inline-flex items-center gap-1">
              <Users size={11} /> 2v2
            </span>
          )}
        </div>
        {event.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 pt-1">
            {event.description}
          </p>
        )}
      </Link>
    </li>
  );
}

function DateBadge({ date }: { date: string }) {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return (
    <div className="shrink-0 text-right">
      <div className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">
        {dt.toLocaleDateString(undefined, { month: "short" })}
      </div>
      <div className="text-lg font-display tabular-nums leading-none">{d}</div>
    </div>
  );
}

// Emits a schema.org ItemList of upcoming events so search engines can
// index the listing. Per-event SportsEvent JSON-LD lives on the detail
// page (rendered server-side).
function SeoBlock({ events }: { events: PublicEventRow[] }) {
  const data = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: events.slice(0, 50).map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://cornholio.app/events/${e.orgSlug ?? ""}/${e.slug}`,
        name: e.name,
      })),
    }),
    [events],
  );
  return (
    <script
      type="application/ld+json"
      // ld+json is a JSON string; React's dangerouslySetInnerHTML is the
      // only way to drop one into <script> without it being escaped.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
